import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { type ChangeEvent, useState } from 'react'

import toast from 'lib/toast'
import { usePostHalts } from 'queries/halts/use-post-halts'
import { track } from 'track'
import { messageFromResponseError } from 'utils/strings'

export const HaltAllAttacksButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [reason, setReason] = useState('')

  const { mutateAsync: haltAllAttacks, isLoading: isHaltAllAttacksLoading } =
    usePostHalts()

  const handleOpen = () => {
    setIsOpen(true)
    setReason('')
  }

  const handleClose = () => {
    setIsOpen(false)
    setReason('')
  }

  const handleChangeReason = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setReason(event.target.value)
  }

  const handleConfirm = async () => {
    try {
      await haltAllAttacks({ reason })

      toast({
        text: 'All tests successfully halted.',
        type: 'success'
      })

      setIsOpen(false)
      const trackingOptions = { attackIds: 'all' }

      track('HaltAllAttacks', trackingOptions)
    } catch (e) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          e,
          'There was an issue halting all tests. Please try again later.'
        )
      })

      setIsOpen(false)
    }
  }

  return (
    <>
      <Dialog
        data-testid="halt-all-dialog"
        open={isOpen}
        sx={{ padding: 2 }}
        onClose={handleClose}
      >
        <DialogTitle>Halt All</DialogTitle>
        <DialogContent>
          <Stack alignItems="start" spacing={2}>
            <Alert severity="warning">
              <Typography sx={{ fontWeight: 'bold' }} variant="inherit">
                This is a company-wide change that will halt all currently
                running tests, experiments, etc. across all teams. Proceed with
                caution.
              </Typography>
            </Alert>
            <TextField
              fullWidth
              multiline
              data-test-input="reason"
              label="Reason (optional)"
              minRows={3}
              name="reason"
              value={reason}
              variant="outlined"
              onChange={handleChangeReason}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
          <Button color="secondary" variant="contained" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="error"
            data-change-confirm-button="Halt All"
            disabled={isHaltAllAttacksLoading}
            variant="contained"
            onClick={handleConfirm}
          >
            {isHaltAllAttacksLoading ? (
              <Stack alignItems="center" direction="row" sx={{ columnGap: 1 }}>
                <Typography variant="inherit">Halting</Typography>
                <CircularProgress color="inherit" size={16} />
              </Stack>
            ) : (
              'Halt All'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Button
        color="error"
        data-testid="halt-all-button"
        variant="contained"
        onClick={handleOpen}
      >
        Halt All
      </Button>
    </>
  )
}
